import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { OrdersTableTab } from '@ekhodealer/ekho-common';
import {
	Badge,
	Button,
	ContentWithTooltip,
	Group,
	Stack,
	Tabs,
	Text,
	TextInput,
	ToggleButton,
} from '@ekhodealer/ekho-common/rev';
import { labelFromOrdersTableTab } from '@ekhodealer/ekho-common/utils';

import type { TabItem } from '@ekhodealer/ekho-common/rev';

import { ExportPopup } from '@common/components/ExportPopup/ExportPopup';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { paths } from '@config/paths';

import { useExportOrders } from '../api/useExportOrders';
import { useSearchOrders } from '../api/useSearchOrders';
import { activeFiltersCountAtom } from '../atoms/ordersAppliedFiltersAtoms';
import {
	actionRequiredFilterAtom,
	currentPageAtom,
	ordersParamsAtom,
	orderTabFilterAtom,
	searchValueAtom,
	selectedOrdersAtom,
	updateActionRequiredFilterAtom,
	updateSearchValueAtom,
} from '../atoms/ordersAtoms';
import { useActionRequiredCount } from '../hooks/useActionRequiredCount';
import { useOrdersTableTabCounts } from '../hooks/useOrdersTableTabCounts';

import { OrdersFiltersDrawer } from './OrdersFiltersDrawer';
import { OrderStats } from './OrderStats';

export function OrdersToolbar() {
	const navigate = useNavigate();
	const routeParams = useParams<{ tabId: OrdersTableTab }>();
	const servicesProviderId = useCurrentServicesProviderId();

	// Filter atoms
	const searchValue = useAtomValue(searchValueAtom);
	const updateSearchValue = useSetAtom(updateSearchValueAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);
	const actionRequiredFilter = useAtomValue(actionRequiredFilterAtom);
	const updateActionRequiredFilter = useSetAtom(updateActionRequiredFilterAtom);

	// Export popup data
	const ordersParams = useAtomValue(ordersParamsAtom);
	const { data } = useSearchOrders(ordersParams);
	const selectedOrders = useAtomValue(selectedOrdersAtom);
	const exportMutation = useExportOrders();

	// Get current active tab from URL params
	const activeTab = routeParams.tabId || OrdersTableTab.ALL;

	// Sync tab filter atom with URL params
	const setOrderTabFilter = useSetAtom(orderTabFilterAtom);
	useEffect(() => {
		const tabFromUrl = routeParams.tabId || OrdersTableTab.ALL;
		setOrderTabFilter(tabFromUrl);
	}, [routeParams.tabId, setOrderTabFilter]);

	// Get tab counts using the hook
	const getTabCount = useOrdersTableTabCounts();

	// Get action required count
	const actionRequiredCount = useActionRequiredCount();

	// Get active filters count from atom
	const activeFiltersCount = useAtomValue(activeFiltersCountAtom);

	// Map tab IDs to their tooltip content
	const tabTooltipMap: Record<OrdersTableTab, string> = {
		[OrdersTableTab.ALL]: 'All orders, across every stage.',
		[OrdersTableTab.PENDING]: 'Placed orders awaiting financing approval or signed contracts.',
		[OrdersTableTab.SALES_PROCESSING]:
			'Payment, paperwork, and risk checks are done. Remaining tasks are prep steps to make the unit ready for delivery or pickup.',
		[OrdersTableTab.KICK_OFF_FULFILLMENT]:
			'Orders with outstanding tasks for the buyer, Ekho, or dealer before the vehicle can be fulfilled.',
		[OrdersTableTab.IN_FULFILLMENT]: 'Orders currently being delivered or picked up.',
		[OrdersTableTab.FULFILLED]:
			'Orders that have been fully delivered or picked up by the buyer. All tasks are complete.',
		[OrdersTableTab.CANCELED]: 'Orders that have been canceled and are no longer active.',
	};

	// Build tab items with tooltips and counts
	const tabItems: TabItem[] = Object.values(OrdersTableTab).map((tab: OrdersTableTab) => {
		const count = getTabCount(tab);
		return {
			id: tab,
			content: (
				<ContentWithTooltip
					content={
						<div>
							<Group itemsSpacing='8' itemsAlignY='center' noWrap>
								<Group.Item>
									<span>{labelFromOrdersTableTab(tab)}</span>
								</Group.Item>
								<Group.Item>
									<Text size='footnote' color='secondary'>
										{count.toLocaleString()}
									</Text>
								</Group.Item>
							</Group>
						</div>
					}
					tooltipContent={tabTooltipMap[tab]}
					tooltipProps={{ offset: 16 }}
				/>
			),
		};
	});

	const handleTabSelect = (item: TabItem) => {
		if (!servicesProviderId) {
			return;
		}
		// Reset to page 1 when user clicks a tab
		setCurrentPage(1);
		navigate(paths.ordersTab.getHref(servicesProviderId, item.id as OrdersTableTab));
	};

	const handleSearchChange = (value: string) => {
		updateSearchValue(value);
	};

	const handleActionRequiredToggle = (isSelected: boolean) => {
		updateActionRequiredFilter(isSelected);
	};

	const allFiltersButton = (
		<Button variant='secondary' size='medium'>
			All filters
		</Button>
	);

	return (
		<Stack itemsSpacing='16'>
			<OrderStats />
			<Tabs items={tabItems} selectedKey={activeTab} onSelect={handleTabSelect} />
			<Group itemsAlignY='center' itemsAlignX='space-between' noWrap>
				<Group.Item>
					<TextInput
						placeholder='Search'
						type='search'
						size='small'
						value={searchValue}
						onChange={handleSearchChange}
					/>
				</Group.Item>
				<Group.Item>
					<Group itemsSpacing='8' noWrap>
						<ToggleButton
							variant='outline'
							size='medium'
							isDisabled={actionRequiredCount === 0}
							isSelected={actionRequiredFilter}
							onChange={handleActionRequiredToggle}>
							{`Action required (${actionRequiredCount})`}
						</ToggleButton>
						<ExportPopup
							resourceName='orders'
							currentPageItems={data?.data ?? []}
							totalCount={data?.count ?? 0}
							selectedItems={selectedOrders}
							getItemId={(item) => item.purchasedVehicleId}
							onExport={(ids) =>
								exportMutation.mutateAsync({ purchasedVehicleIds: ids })
							}
							isPending={exportMutation.isPending}>
							<Button variant='secondary' size='medium'>
								Export
							</Button>
						</ExportPopup>
						<OrdersFiltersDrawer
							trigger={
								activeFiltersCount > 0 ? (
									<Badge
										label={String(activeFiltersCount)}
										appearance='negative'
										variant='solid'
										size='small'>
										{allFiltersButton}
									</Badge>
								) : (
									allFiltersButton
								)
							}
						/>
					</Group>
				</Group.Item>
			</Group>
		</Stack>
	);
}
